import { create } from 'zustand';
import type { AuthResult, MeDTO } from '@moods/shared';
import { configureApi, refreshSession } from '../lib/api';
import { IS_DESKTOP } from '../lib/env';
import { desktopTokenHooks, storeDesktopDeviceToken, storeDesktopRefreshToken } from '../lib/desktop-bridge';

/** The API sets a readable `moods_hint` cookie while a session exists; without it we know we're signed out. */
const hasSessionHint = () => typeof document !== 'undefined' && /(?:^|;\s*)moods_hint=/.test(document.cookie);

type SessionResult = Extract<AuthResult, { kind: 'session' }>;

export interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous';
  accessToken: string | null;
  me: MeDTO | null;
  /** route to return to after a session expired (re-login preserving route, spec §11.8) */
  expiredFrom: string | null;
  setSession: (r: SessionResult) => void;
  setMe: (me: MeDTO) => void;
  clear: (expired?: boolean) => void;
  bootstrap: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading',
  accessToken: null,
  me: null,
  expiredFrom: null,
  setSession: r => {
    // desktop: the API returns the refresh token in the body (cookies do not cross from app://) — keep it encrypted
    storeDesktopRefreshToken(r.refreshToken);
    // …and the "known device" token, so the next login on this computer skips the new-device e-mail
    storeDesktopDeviceToken(r.deviceToken);
    set({ status: 'authenticated', accessToken: r.accessToken, me: r.user, expiredFrom: null });
  },
  setMe: me => set({ me }),
  clear: expired => {
    storeDesktopRefreshToken(null);
    set({
      status: 'anonymous',
      accessToken: null,
      me: null,
      expiredFrom: expired && typeof location !== 'undefined' ? location.pathname + location.search : null,
    });
  },
  bootstrap: async () => {
    if (get().status === 'authenticated') return;
    if (!IS_DESKTOP && !hasSessionHint()) {
      set({ status: 'anonymous', accessToken: null, me: null });
      return;
    }
    const r = await refreshSession();
    if (!r) set({ status: 'anonymous', accessToken: null, me: null });
  },
}));

configureApi({
  getToken: () => useAuth.getState().accessToken,
  onRefreshed: r => useAuth.getState().setSession(r),
  onExpired: () => useAuth.getState().clear(true),
  ...desktopTokenHooks,
});

/** Proactive refresh one minute before the 15-minute access token expires. */
let timer: ReturnType<typeof setTimeout> | null = null;
useAuth.subscribe((s, prev) => {
  if (s.accessToken === prev.accessToken) return;
  if (timer) clearTimeout(timer);
  if (s.accessToken) timer = setTimeout(() => void refreshSession(), 14 * 60_000);
});
